package com.flowyride.data.routing.flowyride

import com.flowyride.core.api.RoutesApi
import com.flowyride.core.api.TrackGuidanceRequestDto
import com.flowyride.core.api.TrackGuidanceResponseDto
import com.flowyride.core.api.toDomain
import com.flowyride.core.api.toDto
import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.Route
import com.flowyride.core.domain.model.RouteEngineId
import com.flowyride.core.domain.model.RouteRequest
import com.flowyride.core.domain.routing.RouteEngine
import com.flowyride.core.domain.routing.TrackGuidance
import com.flowyride.core.domain.routing.TrackGuide
import com.flowyride.core.api.RouteResponseDto
import com.flowyride.data.routing.internal.routingCall
import com.flowyride.data.routing.internal.toServerRoutingException
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.timeout
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.contentType
import io.ktor.http.isSuccess

/**
 * [RouteEngine] backed by the rider's own FlowyRide server, which holds the TomTom key.
 *
 * @param baseUrl e.g. `https://flowyride.example.org`; blank disables the engine.
 */
class FlowyRideServerEngine(
    private val client: HttpClient,
    private val baseUrl: String,
) : RouteEngine, TrackGuidance {

    override val id: RouteEngineId = ID
    override val displayName: String = "FlowyRide"
    override val isAvailable: Boolean get() = baseUrl.isNotBlank()
    override val supportsPreferences: Boolean = true

    /** The server forwards to TomTom, whose Calculate Route API accepts 150 points. */
    override val maxWaypoints: Int = 150

    override suspend fun computeRoute(request: RouteRequest): Route = routingCall {
        val response = client.post(baseUrl.trimEnd('/') + RoutesApi.PATH) {
            contentType(ContentType.Application.Json)
            setBody(request.toDto())
        }
        if (!response.status.isSuccess()) throw response.toServerRoutingException(maxWaypoints)
        response.body<RouteResponseDto>().toDomain(id)
    }

    override suspend fun guideAlong(track: List<GeoPoint>): TrackGuide = routingCall {
        val response = client.post(baseUrl.trimEnd('/') + RoutesApi.GUIDANCE_PATH) {
            contentType(ContentType.Application.Json)
            setBody(TrackGuidanceRequestDto(track.map { it.toDto() }))
            // Long files take several engine calls on the server.
            timeout {
                requestTimeoutMillis = GUIDANCE_TIMEOUT_MILLIS
                socketTimeoutMillis = GUIDANCE_TIMEOUT_MILLIS
            }
        }
        if (!response.status.isSuccess()) throw response.toServerRoutingException(maxWaypoints)
        val body = response.body<TrackGuidanceResponseDto>()
        TrackGuide(
            maneuvers = body.maneuvers.filter { it.trackIndex in track.indices }.map { it.toDomain() },
            speedLimits = body.speedLimits.toDomain(track.size),
        )
    }

    companion object {
        private const val GUIDANCE_TIMEOUT_MILLIS = 120_000L

        val ID = RouteEngineId("flowyride")
    }
}
